import { HeartPulse, Moon, Footprints, Flame, Activity, RefreshCw } from 'lucide-react';

const fmtSleep = (h) => `${Math.floor(h)}h ${String(Math.round((h % 1) * 60)).padStart(2, '0')}m`;

const runSyncShortcut = () => {
  window.location.href = 'shortcuts://run-shortcut?name=' + encodeURIComponent('NEXA Health Sync');
};

const TZ = 'Asia/Jerusalem';

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: TZ });

// Hourly automations run 05:00–23:00 Israel time.
const nextSyncLabel = () => {
  const h = parseInt(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: TZ }).format(new Date()),
    10,
  ) % 24;
  if (h >= 23 || h < 4) return '05:00';
  return `${String(h + 1).padStart(2, '0')}:00`;
};

export const HealthCard = ({ health, updatedAt }) => {
  const ageMin = updatedAt ? Math.round((Date.now() - new Date(updatedAt).getTime()) / 60000) : null;
  const stale = ageMin == null || ageMin > 75;
  return (
  <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-health-card">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <HeartPulse className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
        <span className="text-xs tracking-[0.2em] uppercase text-white/40">Health · Apple Health</span>
      </div>
      <button
        onClick={runSyncShortcut}
        data-testid="nexa-health-sync-now"
        className={`flex items-center gap-1.5 text-[11px] rounded-full px-3 py-1.5 active:scale-95 transition-transform border ${
          stale
            ? 'text-black bg-sky-400 border-sky-400 font-semibold animate-pulse'
            : 'text-sky-400/90 border-white/10'
        }`}
      >
        <RefreshCw className="w-3 h-3" strokeWidth={2} /> Sync now
      </button>
    </div>
    {!health ? (
      <p className="text-sm text-white/50">
        No data synced today yet. Your iPhone Shortcut sends sleep &amp; activity automatically — ask NEXA
        for the setup steps if you haven't installed it.
      </p>
    ) : (
      <div className="grid grid-cols-2 gap-4">
        {health.sleep_hours != null && (
          <div data-testid="nexa-health-sleep">
            <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-1">
              <Moon className="w-3 h-3" /> Sleep
            </div>
            <p className="nexa-heading text-2xl font-light">{fmtSleep(Number(health.sleep_hours))}</p>
            {health.sleep_start && health.sleep_end && (
              <p className="text-[11px] text-white/35 mt-0.5">
                {health.sleep_start} → {health.sleep_end}
              </p>
            )}
          </div>
        )}
        {health.steps != null && (
          <div data-testid="nexa-health-steps">
            <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-1">
              <Footprints className="w-3 h-3" /> Steps
            </div>
            <p className="nexa-heading text-2xl font-light">{Number(health.steps).toLocaleString()}</p>
            {health.walking_km != null && (
              <p className="text-[11px] text-white/35 mt-0.5">{Number(health.walking_km).toFixed(1)} km</p>
            )}
          </div>
        )}
        {health.active_energy_kcal != null && (
          <div data-testid="nexa-health-energy">
            <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-1">
              <Flame className="w-3 h-3" /> Active energy
            </div>
            <p className="nexa-heading text-2xl font-light">
              {Number(health.active_energy_kcal).toLocaleString()}
              <span className="text-sm text-white/40 ml-1">kcal</span>
            </p>
          </div>
        )}
        {health.resting_hr != null && (
          <div data-testid="nexa-health-hr">
            <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-1">
              <Activity className="w-3 h-3" /> Resting HR
            </div>
            <p className="nexa-heading text-2xl font-light">
              {health.resting_hr}
              <span className="text-sm text-white/40 ml-1">bpm</span>
            </p>
          </div>
        )}
      </div>
    )}
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
      <span className="text-[10px] text-white/35 tracking-wide" data-testid="nexa-health-last-sync">
        {updatedAt
          ? `Last sync ${fmtTime(updatedAt)}${ageMin > 75 ? ` · ${Math.floor(ageMin / 60)}h+ old` : ''}`
          : 'No sync yet today'}
      </span>
      <span className="text-[10px] text-white/35 tracking-wide" data-testid="nexa-health-next-sync">
        Next auto-sync ~{nextSyncLabel()}
      </span>
    </div>
  </div>
  );
};
